import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import api from '../lib/api';
import {
  TrendingDown, Plus, X, Send, Save, Users,
  CheckCircle2, AlertCircle, History, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

function EmailChips({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Email inválido');
      return;
    }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter(x => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={`${testId}-input`}
        />
        <Button size="sm" type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios.</span>}
        {items.map(e => (
          <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => remove(e)} className="hover:bg-red-200 rounded-full p-0.5">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

// Iter 294.d (Carlos): alerta inmediata cuando una OT en ejecución pasa a utilidad negativa.
export default function AlertaUtilidadNegativaSection() {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const p = await api.get('/informes/alerta-utilidad/preview');
      setPreview(p.data);
    } catch {
      toast.error('Error calculando utilidades');
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get('/informes/alerta-utilidad/config'),
        api.get('/informes/alerta-utilidad/log?limit=10'),
      ]);
      setCfg(c.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando alerta de utilidad');
    } finally {
      setLoading(false);
    }
    loadPreview();
  }, [loadPreview]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/informes/alerta-utilidad/config', cfg);
      setCfg(res.data);
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if ((!cfg.to || cfg.to.length === 0) && (!cfg.grupos || cfg.grupos.length === 0)) {
      toast.error('Agrega al menos un destinatario o grupo antes de enviar');
      return;
    }
    if (!window.confirm('Se revisarán las OTs en ejecución y, si hay utilidad negativa, se enviará la alerta AHORA. ¿Continuar?')) return;
    setSending(true);
    try {
      const res = await api.post('/informes/alerta-utilidad/revisar-ahora');
      const d = res.data;
      if (d.sent) {
        toast.success(`Alerta enviada: ${d.negativas} OT(s) con utilidad negativa`);
      } else {
        toast.info(d.reason || d.error || 'Sin OTs negativas');
      }
      api.get('/informes/alerta-utilidad/log?limit=10').then(r => setHistory(r.data?.items || []));
      loadPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando');
    } finally {
      setSending(false);
    }
  };

  const handleToggleExcluir = async (otNumero) => {
    const actual = new Set(cfg.excluir_ots || []);
    if (actual.has(otNumero)) actual.delete(otNumero); else actual.add(otNumero);
    const nueva = Array.from(actual);
    try {
      const res = await api.put('/informes/alerta-utilidad/config', { ...cfg, excluir_ots: nueva });
      setCfg(res.data);
      setPreview((p) => {
        if (!p) return p;
        const ots = p.ots.map((u) => u.numero_ot === otNumero ? { ...u, excluida: !u.excluida } : u);
        return { ...p, ots, negativas: ots.filter((u) => u.utilidad < 0 && !u.excluida).length };
      });
      toast.success(actual.has(otNumero) ? `OT-${otNumero} excluida de la alerta` : `OT-${otNumero} vuelve a considerarse en la alerta`);
    } catch {
      toast.error('Error al actualizar exclusión');
    }
  };

  if (loading || !cfg) {
    return <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>;
  }

  const toggleGrupo = (key) => {
    const arr = new Set(cfg.grupos || []);
    if (arr.has(key)) arr.delete(key); else arr.add(key);
    setCfg({ ...cfg, grupos: Array.from(arr) });
  };
  const grupoActivo = (k) => (cfg.grupos || []).includes(k);
  const GRUPOS_DISPONIBLES = [
    { key: 'gerencia', label: 'Gerencia' },
    { key: 'corporativo', label: 'Corporativo' },
    { key: 'contabilidad', label: 'Contabilidad' },
  ];

  return (
    <Card data-testid="card-alerta-utilidad-negativa">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" /> Alerta — Utilidad Negativa en OTs
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Chequeo automático <strong>cada hora</strong>: si una OT en ejecución pasa a utilidad negativa
              (gastos reales &gt; facturado neto) envía correo inmediato, sin esperar al informe mensual.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{cfg.enabled ? 'Activo' : 'Pausado'}</span>
            <Switch
              checked={cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
              data-testid="alerta-utilidad-switch-enabled"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5">Re-avisar cada (días)</label>
            <Input
              type="number" min={1} max={60}
              value={cfg.resend_days}
              onChange={(e) => setCfg({ ...cfg, resend_days: parseInt(e.target.value, 10) || 7 })}
              data-testid="alerta-utilidad-input-resend"
            />
            <p className="text-[11px] text-muted-foreground mt-0.5">La OT nueva se avisa al instante; si sigue negativa, se repite cada X días.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Grupos de destinatarios
            </label>
            <div className="flex flex-wrap gap-2">
              {GRUPOS_DISPONIBLES.map(g => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => toggleGrupo(g.key)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    grupoActivo(g.key)
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`alerta-utilidad-grupo-${g.key}`}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
          <EmailChips
            label="Destinatarios extra (Para)"
            items={cfg.to || []}
            onChange={(t) => setCfg({ ...cfg, to: t })}
            placeholder="email@chirinosteel.com"
            testId="alerta-utilidad-to"
          />
          <EmailChips
            label="Copia (CC)"
            items={cfg.cc || []}
            onChange={(c) => setCfg({ ...cfg, cc: c })}
            placeholder="copia@chirinosteel.com"
            testId="alerta-utilidad-cc"
          />
        </div>

        <div className="flex flex-wrap gap-2 pt-3 border-t">
          <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="alerta-utilidad-btn-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar configuración'}
          </Button>
          <Button onClick={handleSendNow} disabled={sending} variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50" data-testid="alerta-utilidad-btn-send-now">
            <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Revisando…' : 'Revisar y alertar ahora (test)'}
          </Button>
        </div>

        <div className="pt-2 border-t">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-medium">
              Utilidad actual de las OTs en ejecución
              {preview && <span className="ml-2 text-red-700 font-bold">{preview.negativas} negativa(s)</span>}
            </p>
            <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={loadPreview} data-testid="alerta-utilidad-btn-refresh">
              <RefreshCw className={`w-3 h-3 ${loadingPreview ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {loadingPreview && !preview ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="border rounded max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">OT</th>
                    <th className="text-left px-2 py-1.5">Cliente</th>
                    <th className="text-right px-2 py-1.5">Total Gastos</th>
                    <th className="text-right px-2 py-1.5">Facturado</th>
                    <th className="text-right px-2 py-1.5">Utilidad</th>
                    <th className="text-center px-2 py-1.5">Alerta</th>
                    <th className="text-center px-2 py-1.5" title="OTs internas o excluidas no disparan la alerta">Excluir</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview?.ots || []).map(u => (
                    <tr key={u.numero_ot} className={`border-t ${u.excluida ? 'opacity-50 bg-gray-50' : u.utilidad < 0 ? 'bg-red-50' : ''}`} data-testid={`alerta-utilidad-row-${u.numero_ot}`}>
                      <td className="px-2 py-1 font-mono font-semibold" title={u.descripcion}>
                        OT-{u.numero_ot}
                        {u.excluida && <span className="ml-1 text-[9px] bg-gray-200 text-gray-600 px-1 py-0.5 rounded">EXCLUIDA</span>}
                      </td>
                      <td className="px-2 py-1 truncate max-w-[180px]">{u.cliente_nombre}</td>
                      <td className="px-2 py-1 text-right font-mono">{formatCLP(u.total_gastos)}</td>
                      <td className="px-2 py-1 text-right font-mono">{formatCLP(u.facturado)}</td>
                      <td className={`px-2 py-1 text-right font-mono font-bold ${u.utilidad < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        {formatCLP(u.utilidad)}
                      </td>
                      <td className="px-2 py-1 text-center text-[10px] text-muted-foreground">
                        {u.alertada_at ? new Date(u.alertada_at).toLocaleDateString('es-CL') : '—'}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <Switch
                          checked={!!u.excluida}
                          onCheckedChange={() => handleToggleExcluir(u.numero_ot)}
                          className="scale-75"
                          data-testid={`alerta-utilidad-excluir-${u.numero_ot}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="pt-2 border-t">
          <p className="text-xs font-medium flex items-center gap-1 mb-1.5"><History className="w-3.5 h-3.5" /> Historial de alertas</p>
          {history.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Aún no se han enviado alertas.</p>
          ) : (
            <div className="divide-y border rounded">
              {history.map((h, idx) => (
                <div key={idx} className="px-3 py-2 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    {h.success ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
                    <span>{new Date(h.timestamp).toLocaleString('es-CL')}</span>
                    <Badge variant="outline" className="text-[10px]">{h.manual ? 'manual' : 'auto'}</Badge>
                    <span className="text-muted-foreground">→ {(h.to || []).join(', ')}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {h.negativas_count} OT(s) negativas {(h.nuevas || []).length > 0 && `· nuevas: ${h.nuevas.map(n => `OT-${n}`).join(', ')}`}
                    {h.error && <span className="text-red-600 ml-2">{String(h.error).slice(0, 50)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
